import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Phone, Mail, Globe, CheckCircle, UserPlus, MessageCircle } from 'lucide-react';
import { supabase } from '../config/supabase';
import { Skeleton } from '../components/ui/skeleton';
import Footer from '../components/Footer';
import { traderService, type TraderFeatures, type Trader } from '../services/traderService';
import SocialLinksList from '../components/SocialLinksList';
import FeatureShowcase from '../components/FeatureShowcase';

// Using the same Trader interface but extending it for hospitality-specific fields
type HospitalityBusiness = Trader & { 
  trader_features?: TraderFeatures;
  hospitality_category?: { id: number; name: string; category: string };
};

const HospitalityDetailPage: React.FC = () => {
  const { slug: slugOrId } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [business, setBusiness] = useState<HospitalityBusiness | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Features for this hospitality business from the shared trader_features table
  const [hospitalityFeatures, setHospitalityFeatures] = useState<TraderFeatures | null>(null);

  useEffect(() => {
    const fetchBusiness = async () => {
      if (!slugOrId) {
        setError('No business identifier provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        let businessData = null;
        let fetchedById = false;

        // Try fetching by slug first
        const slugQuery = await supabase
          .from('hospitality')
          .select(`
            *,
            hospitality_categories!inner(id, name, category)
          `)
          .eq('slug', slugOrId)
          .single();

        if (slugQuery.data) {
          businessData = slugQuery.data;
        } else {
          // Fallback: if param looks numeric, try as ID (backwards compatibility)
          const maybeId = Number(slugOrId);
          if (!Number.isNaN(maybeId)) {
            const idQuery = await supabase
              .from('hospitality')
              .select(`
                *,
                hospitality_categories!inner(id, name, category)
              `)
              .eq('id', maybeId)
              .single();

            if (idQuery.data) {
              businessData = idQuery.data;
              fetchedById = true;
            }
          }
        }

        if (!businessData) {
          throw new Error('Hospitality business not found');
        }

        if (fetchedById && businessData.slug && businessData.slug !== slugOrId) {
          navigate(`/hospitality/${businessData.slug}`, { replace: true });
          return;
        }

        // Transform the data to match the expected interface
        const transformedBusiness = {
          ...businessData,
          hospitality_category: businessData.hospitality_categories
        };

        setBusiness(transformedBusiness);
      } catch (err) {
        console.error('Error fetching hospitality business:', err);
        setError('Failed to load business details');
      } finally {
        setLoading(false);
      }
    };

    fetchBusiness();
  }, [slugOrId, navigate]);

  // Load shared feature cards for this hospitality business (keyed by user_id)
  useEffect(() => {
    const fetchFeatures = async () => {
      if (!business?.user_id) return;

      try {
        const features = await traderService.getTraderFeatures(business.user_id);
        setHospitalityFeatures(features);
      } catch (err) {
        console.error('Error fetching hospitality features:', err);
        setHospitalityFeatures(null);
      }
    };

    fetchFeatures();
  }, [business?.user_id]);

  // Build a list of enabled features to render as cards on the detail page
  // If no features exist in database, create default feature based on business data
  const enabledFeatures = React.useMemo(
    () => {
      const fallbackImage = business?.avatar_url || '/bally-Market-sign-200px.png';

      // If no features found in database, create default feature from business data
      if (!hospitalityFeatures) {
        return [] as Array<{ title: string; description: string; image: string }>;
      }

      const features = [
        {
          title: hospitalityFeatures.feature1_title || 'Feature 1',
          description: hospitalityFeatures.feature1_description || '',
          image: hospitalityFeatures.feature1_image || fallbackImage,
          enabled: hospitalityFeatures.feature1_enabled
        },
        {
          title: hospitalityFeatures.feature2_title || 'Feature 2',
          description: hospitalityFeatures.feature2_description || '',
          image: hospitalityFeatures.feature2_image || fallbackImage,
          enabled: hospitalityFeatures.feature2_enabled
        },
        {
          title: hospitalityFeatures.feature3_title || 'Feature 3',
          description: hospitalityFeatures.feature3_description || '',
          image: hospitalityFeatures.feature3_image || fallbackImage,
          enabled: hospitalityFeatures.feature3_enabled
        }
      ].filter(feature => {
        const enabledValue = feature.enabled as any;
        const isEnabled =
          enabledValue === true ||
          enabledValue === 'true' ||
          enabledValue === '1' ||
          enabledValue === 1;

        return isEnabled;
      });

      return features as Array<{ title: string; description: string; image: string }>;
    },
    [hospitalityFeatures, business?.avatar_url, business?.store_name, business?.owner_name, business?.description]
  );

  const handleBack = () => {
    navigate(-1);
  };

  const hasLocation = Boolean(business?.location && business.location.trim().length > 0);
  const shouldShowMap = business ? (((business as any).show_map ?? true) as boolean) : false;
  const mapUrl = hasLocation && shouldShowMap && business
    ? `https://www.google.com/maps?q=${encodeURIComponent(`${business.location}, Ireland`)}&output=embed`
    : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-6 mt-16">
            <Skeleton className="h-6 w-24" />
          </div>
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-8 w-48" />
                  <Skeleton className="h-6 w-6 rounded-full" />
                </div>
                <Skeleton className="h-6 w-32" />
                <div className="space-y-3">
                  <Skeleton className="h-5 w-full" />
                  <Skeleton className="h-5 w-full" />
                  <Skeleton className="h-5 w-3/4" />
                </div>
              </div>
              <div className="flex justify-center">
                <Skeleton className="w-full max-w-sm aspect-square rounded-lg" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !business) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Business Not Found</h2>
            <p className="text-gray-600 mb-6">
              {error || "The hospitality business you're looking for doesn't exist or may have been removed."}
            </p>
            <button
              onClick={handleBack}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={handleBack}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6 mt-16"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to results
        </button>

        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 space-y-8">
            <div className="flex flex-col gap-8 lg:grid lg:grid-cols-2 lg:gap-10">
              {/* Business Name & Category */}
              <section className="space-y-3 lg:col-span-2">
                <div className="flex items-center gap-3">
                  <h1 className="text-3xl font-bold text-gray-900">
                    {business.store_name || business.owner_name}
                  </h1>
                  {business.verified && (
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  )}
                </div>
                <p className="text-sm uppercase tracking-wide text-gray-500">
                  {business.hospitality_category?.name || 'Hospitality Business'}
                </p>
              </section>

              {/* Image / Avatar */}
              <section>
                <div className="w-full max-w-sm mx-auto aspect-square rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                  {business.avatar_url ? (
                    <img
                      src={business.avatar_url}
                      alt={business.store_name || business.owner_name}
                      className="w-full h-full object-cover object-center"
                    />
                  ) : (
                    <img
                      src={`https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(business.store_name || business.owner_name)}&backgroundColor=f3f4f6&fontSize=40`}
                      alt={business.store_name || business.owner_name}
                      className="w-full h-full object-cover object-center"
                    />
                  )}
                </div>
              </section>

              {/* Contact Details */}
              <section className="space-y-4">
                {business.phone_number && (
                  <p className="flex items-center text-gray-700">
                    <Phone className="w-4 h-4 mr-2 text-gray-400" />
                    <span className="font-medium mr-2">Phone:</span>
                    <a 
                      href={`tel:${business.phone_number}`}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      {business.phone_number}
                    </a>
                  </p>
                )}
                
{/* Only show email if it's not a fake placeholder */}
                {business.email && !business.email.endsWith('@unclaimed.ballymarket.local') && (
                  <p className="flex items-center text-gray-700">
                    <Mail className="w-4 h-4 mr-2 text-gray-400" />
                    <span className="font-medium mr-2">Email:</span>
                    <a 
                      href={`mailto:${business.email}`}
                      className="text-blue-600 hover:text-blue-800 truncate"
                    >
                      {business.email}
                    </a>
                  </p>
                )}
                
                {business.website && (
                  <p className="flex items-center text-gray-700">
                    <Globe className="w-4 h-4 mr-2 text-gray-400" />
                    <span className="font-medium mr-2">Website:</span>
                    <a 
                      href={business.website.startsWith('http') ? business.website : `https://${business.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 truncate"
                    >
                      {business.website}
                    </a>
                  </p>
                )}

                <SocialLinksList userId={business.user_id} className="mt-2" />
                
                <p className="flex items-center text-gray-700">
                  <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                  <span className="font-medium mr-2">Location:</span>
                  {business.location}
                </p>
              </section>
            </div>

            {/* About Section */}
            <section className="pt-4 border-t border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">About</h2>
              <p className="text-gray-700 whitespace-pre-wrap">
                {business.description || 'No description available.'}
              </p>
            </section>

            {/* Featured Offerings Section */}
            {enabledFeatures.length > 0 && (
              <section className="pt-4 border-t border-gray-200">
                <FeatureShowcase
                  title={hospitalityFeatures?.features_section_title || 'Featured Offerings'}
                  features={enabledFeatures}
                  layout={hospitalityFeatures?.features_layout || 'rows'}
                />
              </section>
            )}

            {/* Map Section */}
            {mapUrl && (
              <section className="pt-4 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Location Map</h3>
                <div className="w-full overflow-hidden rounded-lg border border-gray-200">
                  <iframe
                    title={`Map for ${business.store_name || business.owner_name}`}
                    src={mapUrl}
                    width="100%"
                    height="260"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </section>
            )}

            {/* Claim Your Business Section - Only show for unclaimed businesses */}
            {business.mockup && !business.user_id && (
              <section className="pt-4 border-t border-gray-200">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 text-center">
                  {business.email?.endsWith('@unclaimed.ballymarket.local') ? (
                    // Business has fake email - show contact us message
                    <>
                      <MessageCircle className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                      <h2 className="text-xl font-semibold text-gray-900 mb-2">
                        Is this your business?
                      </h2>
                      <p className="text-gray-600 mb-4 max-w-md mx-auto">
                        We don't have contact details for this business on file. 
                        If you're the owner, please contact us to verify your ownership and claim this listing.
                      </p>
                      <a
                        href="mailto:info@ballymarket.ie?subject=Business%20Claim%20Request" 
                        className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Mail className="w-5 h-5 mr-2" />
                        Contact Us to Claim
                      </a>
                      <p className="text-sm text-gray-500 mt-3">
                        Email us at <strong>info@ballymarket.ie</strong> with proof of ownership
                      </p>
                    </>
                  ) : (
                    // Business has real email - show claim button
                    <>
                      <UserPlus className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                      <h2 className="text-xl font-semibold text-gray-900 mb-2">
                        Is this your business?
                      </h2>
                      <p className="text-gray-600 mb-4 max-w-md mx-auto">
                        Claim this listing to get full access to your business profile. 
                        You'll be able to update your information, add photos, and manage your presence on BallyMarket.
                      </p>
                      <button
                        onClick={() => navigate(`/claim/${business.id}`)}
                        className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <UserPlus className="w-5 h-5 mr-2" />
                        Claim Your Business
                      </button>
                      {(business.email?.toLowerCase().endsWith('@gmail.com') || business.email?.toLowerCase().endsWith('@googlemail.com')) && (
                        <p className="text-sm text-gray-500 mt-3">
                          You can sign up with your Google account ({business.email})
                        </p>
                      )}
                    </>
                  )}
                </div>
              </section>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default HospitalityDetailPage;
